/**
 * Invite management (SPEC.md §2.2, §11 item 16) — the admin-only half of onboarding a
 * friend. `AdminDep` server-side, so this 403s for anyone who isn't the owner; rather than
 * hide the card outright (a friend who's added it to their own Settings layout should see
 * *why* it's empty, not wonder if it's broken), it renders a short explanation instead.
 *
 * EFSS never sends mail (`routers/auth.py`) — the plaintext code is shown here exactly once,
 * the same "copy this now" contract `TokensCard` already uses for a minted token, and it's
 * up to whoever created it to hand it over however they'd already talk to that person.
 *
 * The username below is a label for the owner's own reference, not a lock (DECISIONS.md
 * D192) — the code is what `register()` actually checks, so it works for any address.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type Invite } from "../api";
import { inviteUrl } from "../tokenUrl";
import { EyeToggle } from "./EyeToggle";
import { QrCode } from "./QrCode";

function when(epoch: number): string {
  return new Date(epoch * 1000).toLocaleString();
}

type Copied = "link-copied" | "link-selected" | "code-copied" | "code-selected" | null;

export function InvitesCard() {
  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [copied, setCopied] = useState<Copied>(null);
  const linkText = useRef<HTMLElement>(null);
  const codeText = useRef<HTMLElement>(null);
  const [revealed, setRevealed] = useState<{ username: string; code: string } | null>(null);
  const [showUsed, setShowUsed] = useState(false);

  const load = useCallback(async (includeUsed: boolean) => {
    try {
      setInvites((await api.invites(includeUsed)).invites);
      setForbidden(false);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
      setInvites([]);
    }
  }, []);

  useEffect(() => {
    void load(showUsed);
  }, [load, showUsed]);

  async function send(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = username.trim();
    if (!trimmed) return;

    try {
      const created = await api.createInvite(trimmed);
      setRevealed({ username: created.username, code: created.code });
      setUsername("");
      await load(showUsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function revoke(invite: Invite) {
    if (!window.confirm(`Revoke the invite for ${invite.username}? The code will stop working.`)) {
      return;
    }
    try {
      // Cancelling an unused invite deletes the row outright (unlike a token/session's soft
      // revoke), so there's no crossed-out state to preserve here — a re-fetch is correct.
      await api.revokeInvite(invite.id);
      await load(showUsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  /** Returns whether the clipboard write actually happened, so each caller can fall back to
   * selecting its own text node rather than a shared one — copying the link must not select
   * the bare code instead, or vice versa. */
  async function copyText(text: string): Promise<boolean> {
    try {
      if (window.isSecureContext && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Permission denied, or no clipboard. Fall through to selecting it.
    }
    return false;
  }

  function select(node: HTMLElement | null) {
    if (!node) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  async function copyLink() {
    if (!revealed) return;
    if (await copyText(inviteUrl(revealed.code))) {
      setCopied("link-copied");
    } else {
      select(linkText.current);
      setCopied("link-selected");
    }
  }

  async function copyCode() {
    if (!revealed) return;
    if (await copyText(revealed.code)) {
      setCopied("code-copied");
    } else {
      select(codeText.current);
      setCopied("code-selected");
    }
  }

  if (forbidden) {
    return (
      <section className="panel">
        <header>
          <h2>Invites</h2>
        </header>
        <p className="muted small">Only the owner can send invites.</p>
      </section>
    );
  }

  return (
    <>
      {revealed && (
        <section className="panel">
          <div className="token-reveal">
            <strong>Copy this now - it is shown once and never again.</strong>
            <p className="small muted" style={{ margin: "0.4rem 0" }}>
              The server stores only a hash - if you lose this, revoke it and create a new
              one rather than looking for it again. Send it to {revealed.username} however
              you'd normally reach them; eFss never emails anyone.
            </p>
            <div className="reveal-body">
              <div>
                <p>
                  <code ref={linkText}>{inviteUrl(revealed.code)}</code>
                </p>
                <p className="small muted" style={{ margin: 0 }}>
                  Opens straight to the register form, code already filled in - the fastest
                  way in if you're texting or messaging this to them.
                </p>
              </div>
              <QrCode
                url={inviteUrl(revealed.code)}
                label="QR code that opens the register form with this invite code filled in"
                caption="Scan on their phone to skip typing anything."
              />
            </div>
            <div className="row wrap">
              <button className="btn small" onClick={() => void copyLink()}>
                {copied === "link-copied"
                  ? "Copied"
                  : copied === "link-selected"
                    ? "Selected - press Ctrl+C"
                    : "Copy link"}
              </button>
              <button className="btn ghost small" onClick={() => setRevealed(null)}>
                Done
              </button>
            </div>
            <p className="small muted" style={{ margin: "0.6rem 0 0.2rem" }}>
              Or just the code, if they're registering by hand:{" "}
              <code ref={codeText}>{revealed.code}</code>
            </p>
            <button className="btn ghost small" onClick={() => void copyCode()}>
              {copied === "code-copied"
                ? "Copied"
                : copied === "code-selected"
                  ? "Selected - press Ctrl+C"
                  : "Copy code"}
            </button>
          </div>
        </section>
      )}

      <section className="panel">
        <header>
          <h2>Invite a friend</h2>
        </header>

        <form onSubmit={(e) => void send(e)} style={{ display: "grid", gap: "0.6rem" }}>
          <input
            className="input"
            type="text"
            placeholder="Their name"
            aria-label="Invite username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <p className="muted small" style={{ margin: 0 }}>
            Just a label for your own reference - anyone with the code can register with it,
            any username. Nothing gets sent; you hand the code over yourself.
          </p>
          <button className="btn primary" type="submit" disabled={!username.trim()}>
            Create invite
          </button>
        </form>
      </section>

      <section className="panel">
        <header>
          <h2>Pending and used</h2>
          <EyeToggle open={showUsed} onToggle={() => setShowUsed((v) => !v)} label="used invites" />
        </header>

        {error && <p className="error">{error}</p>}
        {invites === null ? (
          <p className="muted">Loading…</p>
        ) : invites.length === 0 ? (
          <p className="muted small">No invites yet.</p>
        ) : (
          <table className="tokens">
            <thead>
              <tr>
                <th>Name</th>
                <th>Sent</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => {
                const expired = !invite.used_at && invite.expires_at * 1000 < Date.now();
                return (
                  <tr key={invite.id} className={invite.used_at ? "revoked" : undefined}>
                    <td>{invite.username}</td>
                    <td className="small muted">{when(invite.created_at)}</td>
                    <td className="small">
                      {invite.used_at
                        ? `Registered ${when(invite.used_at)}`
                        : expired
                          ? "Expired"
                          : "Pending"}
                    </td>
                    <td>
                      {!invite.used_at && !expired && (
                        <button className="btn ghost small" onClick={() => void revoke(invite)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
